/**
 * LoginPage Component
 *
 * Handles user authentication and initial setup for fresh instances.
 * Displays login form or setup form based on auth state.
 */

import { useState, useEffect, FormEvent } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { getCustomerName, getProductName, useRuntimeConfigStore } from '@/stores/runtimeConfigStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { AlertCircle, Loader2, UserPlus } from 'lucide-react';

// ============================================
// Types
// ============================================

interface FormState {
  username: string;
  password: string;
  confirmPassword: string;
}

// ============================================
// Component
// ============================================

export function LoginPage() {
  const {
    isLoading,
    needsSetup,
    error,
    initialized,
    checkStatus,
    login,
    setup,
    clearError,
  } = useAuthStore();
  const {
    config: runtimeConfig,
    initialized: runtimeConfigInitialized,
    error: runtimeConfigError,
  } = useRuntimeConfigStore();

  const [formState, setFormState] = useState<FormState>({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  // Check auth status on mount
  useEffect(() => {
    if (!initialized) {
      checkStatus();
    }
  }, [initialized, checkStatus]);

  // Clear errors when switching between login/setup or when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    if (validationError) {
      setValidationError(null);
    }
    // Only run when form values change, not on error changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.username, formState.password, formState.confirmPassword]);

  const handleInputChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormState((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validateForm = (): boolean => {
    const { username, password, confirmPassword } = formState;

    if (!username.trim()) {
      setValidationError('Username is required');
      return false;
    }

    if (needsSetup && username.trim().length < 3) {
      setValidationError('Username must be at least 3 characters');
      return false;
    }

    if (!password) {
      setValidationError('Password is required');
      return false;
    }

    if (needsSetup && password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return false;
    }

    if (needsSetup && password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!validateForm()) {
      return;
    }

    const { username, password } = formState;

    if (needsSetup) {
      await setup(username.trim(), password);
    } else {
      await login(username.trim(), password);
    }
  };

  // Show loading state while checking initial auth status
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-surface-600 dark:text-surface-400">Loading...</p>
        </div>
      </div>
    );
  }

  const displayError = validationError || error;
  const productName = getProductName(runtimeConfig);
  const customerName = runtimeConfigInitialized && !runtimeConfigError
    ? getCustomerName(runtimeConfig)
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <BrandLogo className="h-20 w-20" />
          </div>
          <div className="mb-3 space-y-1">
            {customerName && (
              <h1 className="text-2xl font-semibold text-surface-950 dark:text-surface-50">
                {customerName}
              </h1>
            )}
            <p className="text-sm font-medium text-surface-700 dark:text-surface-300">
              {customerName ? `${productName} tenant workspace` : productName}
            </p>
          </div>
          {runtimeConfigError && (
            <p className="mb-3 text-xs text-amber-700 dark:text-amber-300">
              Tenant identity is unavailable. Showing product identity only.
            </p>
          )}
          <p className="text-surface-600 dark:text-surface-400">
            {needsSetup
              ? 'Create your admin account to get started'
              : 'Sign in to your account'}
          </p>
        </div>

        {/* Form Card */}
        <div className="card p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error Alert */}
            {displayError && (
              <div className="flex items-start gap-3 p-3 rounded-md bg-destructive-50 dark:bg-destructive-950/30 border border-destructive-200 dark:border-destructive-800">
                <AlertCircle className="h-5 w-5 text-destructive-600 dark:text-destructive-400 shrink-0 mt-0.5" />
                <p className="text-sm text-destructive-700 dark:text-destructive-300">
                  {displayError}
                </p>
              </div>
            )}

            {/* Username Field */}
            <div className="space-y-2">
              <label
                htmlFor="username"
                className="block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Username
              </label>
              <Input
                id="username"
                type="text"
                value={formState.username}
                onChange={handleInputChange('username')}
                placeholder="Enter your username"
                autoComplete="username"
                autoFocus
                disabled={isLoading}
                className="focus-visible:ring-primary-500"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Password
              </label>
              <PasswordInput
                id="password"
                value={formState.password}
                onChange={handleInputChange('password')}
                placeholder={needsSetup ? 'Create a password (min 8 chars)' : 'Enter your password'}
                autoComplete={needsSetup ? 'new-password' : 'current-password'}
                disabled={isLoading}
                className="focus-visible:ring-primary-500"
              />
            </div>

            {/* Confirm Password Field (Setup only) */}
            {needsSetup && (
              <div className="space-y-2">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Confirm Password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  value={formState.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  disabled={isLoading}
                  className="focus-visible:ring-primary-500"
                />
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {needsSetup ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : needsSetup ? (
                <>
                  <UserPlus className="h-4 w-4" />
                  Create Account
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-surface-500 dark:text-surface-500">
            {needsSetup
              ? 'This is a fresh instance. Create your admin credentials above.'
              : 'Contact your administrator if you need access.'}
          </p>
          <p className="text-xs text-surface-400 dark:text-surface-600 flex items-center justify-center gap-1.5">
            Powered by
            <BrandLogo className="w-4 h-4 inline-block" />
            <span className="font-medium">{productName}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
